// Combined blockchain adapter
import { useCallback } from 'react'
import { useEthereumAdapter } from './ethereumAdapter'
import { useTronAdapter } from './tronAdapter'
import { BlockchainAdapter, BlockchainType } from './types'

type T =
  | ({ isConnected: false } & Partial<BlockchainAdapter>)
  | ({ isConnected: true } & BlockchainAdapter)

export function useBlockchainAdapter(): Omit<T, 'connect'> & {
  type: BlockchainType | undefined
  connect: (blockchain?: BlockchainType, connector?: string) => void
} {
  const ethereumAdapter = useEthereumAdapter()
  const tronAdapter = useTronAdapter()

  const selectedBlockchain = localStorage.getItem('blockchain')
  const getActiveAdapter = useCallback(() => {
    if (tronAdapter.isConnected) return tronAdapter
    if (ethereumAdapter.isConnected) return ethereumAdapter
    if (selectedBlockchain === 'tron') return tronAdapter
    if (selectedBlockchain === 'ethereum') return ethereumAdapter
    return null
  }, [tronAdapter, ethereumAdapter, selectedBlockchain])

  const activeAdapter = getActiveAdapter()

  const connect = useCallback(
    (blockchain?: BlockchainType, connector?: string) => {
      const selectedBlockchain =
        blockchain ?? (localStorage.getItem('blockchain') as BlockchainType)
      const selectedConnector = connector ?? localStorage.getItem('connector')
      if (!selectedBlockchain || !selectedConnector) return
      localStorage.setItem('blockchain', selectedBlockchain)
      localStorage.setItem('connector', selectedConnector)
      const adapter = selectedBlockchain === BlockchainType.TRON ? tronAdapter : ethereumAdapter
      adapter.connect(selectedConnector)
    },
    [activeAdapter, activeAdapter?.connect],
  )

  const disconnect = useCallback(() => {
    localStorage.removeItem('blockchain')
    localStorage.removeItem('connector')
    activeAdapter?.disconnect()
  }, [activeAdapter])

  return {
    isConnected: false,
    ...activeAdapter,
    connect,
    disconnect,
    type:
      activeAdapter === tronAdapter
        ? BlockchainType.TRON
        : activeAdapter === ethereumAdapter
        ? BlockchainType.ETHEREUM
        : undefined,
  }
}
